/**
 * Braze Web SDK client-side helpers.
 *
 * Thin wrappers around `@braze/web-sdk` that guard against the SDK not
 * being initialised (e.g. missing API key, consent denied, SSR context).
 *
 * **Consent posture (PECR / GDPR)**
 *
 * Braze is a customer-engagement / marketing platform. Per the most-
 * compliant interpretation of MWP-449, the entire Braze Web SDK — both
 * loading and any subsequent `capture` / `identify` calls — is gated on
 * the **advertising** (marketing) consent category, not analytics. The
 * SDK is never loaded before the user explicitly grants marketing
 * consent via the cookie banner / settings modal.
 *
 * All functions in this module are safe to call before the SDK is
 * initialised — they are no-ops when the global `braze` instance is
 * absent. This means call sites can use them unconditionally.
 *
 * @see src/modules/analytics/components/braze/index.tsx — SDK loader
 * @see src/lib/analytics/consent.ts — opt-in / opt-out wiring
 */

import type * as braze from '@braze/web-sdk';

/**
 * Reference to the loaded Braze SDK instance, set by the `Braze` loader
 * component after the user grants marketing consent. `undefined` until
 * the dynamic import resolves, ensuring no `@braze/web-sdk` code runs
 * before consent.
 */
let brazeInstance: typeof braze | undefined;

/**
 * Register the loaded SDK instance. Called exactly once by the `Braze`
 * loader component after the dynamic import + `initialize()` succeed.
 *
 * Subsequent calls overwrite the previous instance — useful for hot-
 * reloading during development. In production, the loader only mounts
 * once per session.
 */
export const setBrazeInstance = (instance: typeof braze): void => {
  brazeInstance = instance;
};

/** Clear the SDK instance reference (used on consent withdrawal). */
export const clearBrazeInstance = (): void => {
  brazeInstance = undefined;
};

/** Whether the SDK has been loaded and initialised. */
export const isBrazeLoaded = (): boolean => brazeInstance !== undefined;

/**
 * Capture a custom event in Braze.
 *
 * Safe to call on the server (no-op) or before the SDK loads.
 *
 * @param eventName  Event name (e.g. "add_to_cart", "purchase")
 * @param properties Optional flat property bag — must be JSON-serialisable
 */
export const brazeCapture = (
  eventName: string,
  properties?: Record<string, unknown>,
): void => {
  if (!brazeInstance) {
    return;
  }

  try {
    brazeInstance.logCustomEvent(eventName, properties);
  } catch {
    // SDK errors must never break analytics fan-out — swallow silently.
  }
};

/**
 * Identify the current user in Braze after login or registration.
 *
 * **Only called when marketing (advertising) consent is granted** — the
 * loader component will not have set `brazeInstance` otherwise. The
 * `external_id` matches the Medusa customer ID used by the server-side
 * Braze connector, so client- and server-side data merge on the same
 * user profile.
 *
 * @param customerId  Medusa customer ID (becomes Braze `external_id`)
 * @param traits      Optional person attributes (email, first_name, etc.)
 */
export const brazeIdentify = (
  customerId: string,
  traits?: { email?: string; first_name?: string; last_name?: string },
): void => {
  if (!brazeInstance) {
    return;
  }

  try {
    brazeInstance.changeUser(customerId);

    const user = brazeInstance.getUser();
    if (!user) {
      return;
    }

    if (traits?.email) {
      user.setEmail(traits.email);
    }
    if (traits?.first_name) {
      user.setFirstName(traits.first_name);
    }
    if (traits?.last_name) {
      user.setLastName(traits.last_name);
    }
  } catch {
    // SDK errors must never break the auth flow — swallow silently.
  }
};

/**
 * Set an arbitrary custom user attribute on the current Braze user.
 *
 * Use sparingly — every attribute lands on the user profile and counts
 * against Braze data points. Most data should travel via `brazeCapture`
 * as event properties (ephemeral) rather than as profile attributes
 * (persistent + segmentable).
 */
export const brazeSetAttribute = (key: string, value: unknown): void => {
  if (!brazeInstance) {
    return;
  }

  try {
    brazeInstance.getUser()?.setCustomUserAttribute(key, value as never);
  } catch {
    // Swallow silently.
  }
};

/**
 * Reset Braze identity on sign-out so subsequent events on this
 * browser are no longer associated with the previously identified
 * customer.
 *
 * The Braze Web SDK has no "clear external_id" primitive: once
 * `changeUser(id)` has been called, every following call (including
 * `openSession()`) keeps writing to that profile. To genuinely break
 * the association we follow Braze's documented sign-out recipe:
 *
 *   1. `wipeData()` — deletes the cached external_id, anonymous id,
 *      device id and queued events. Also implicitly disables the SDK.
 *   2. `enableSDK()` — re-enables the SDK so anonymous tracking can
 *      resume on the now-clean device profile.
 *   3. `openSession()` — starts a fresh anonymous session.
 *
 * The user remains under marketing consent (this is sign-out, not
 * opt-out) so the SDK instance is kept and the device continues to
 * collect anonymous events until either a new `brazeIdentify` call
 * or marketing-consent withdrawal.
 */
export const brazeReset = (): void => {
  if (!brazeInstance) {
    return;
  }

  // Each step is wrapped independently so a transient failure in one
  // doesn't silently skip the others. In particular: if `wipeData()`
  // throws, we must still attempt `enableSDK()` + `openSession()`,
  // otherwise the instance reference would remain pointing at the
  // previously identified SDK and subsequent `brazeCapture` calls
  // would silently keep writing to the old customer profile.
  try {
    brazeInstance.wipeData();
  } catch {
    // Swallow.
  }
  try {
    brazeInstance.enableSDK();
  } catch {
    // Swallow.
  }
  try {
    brazeInstance.openSession();
  } catch {
    // Swallow.
  }
};

/**
 * Opt the user out of Braze entirely. Called from `applyPreferences()`
 * in `consent.ts` when the user withdraws marketing consent.
 *
 * Wipes all locally-stored Braze data (device ID, anonymous ID, queued
 * events) so the next opt-in starts fresh.
 */
export const brazeOptOut = (): void => {
  if (!brazeInstance) {
    return;
  }

  // Each step is wrapped independently and the instance reference is
  // only cleared once `wipeData()` has actually succeeded — otherwise
  // a transient `disableSDK()` failure would skip the wipe AND drop
  // our handle on the SDK, leaving the user's local Braze data
  // (device id, queued events, cached external_id) in place with no
  // way for a future opt-out call to retry the cleanup. The wipe is
  // the load-bearing part of the opt-out contract — disable is
  // best-effort because the SDK can't send anything once the
  // instance reference is gone anyway.
  try {
    brazeInstance.disableSDK();
  } catch {
    // Swallow — the wipe below is what actually enforces opt-out.
  }

  let wiped = false;
  try {
    brazeInstance.wipeData();
    wiped = true;
  } catch {
    // Swallow — leave the reference intact so a subsequent opt-out
    // call (or page reload) can retry.
  }

  if (wiped) {
    clearBrazeInstance();
  }
};
